import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

export default function Gallery() {
  const [galleries, setGalleries] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('Alle');
  const [activeImage, setActiveImage] = useState(null); // Lightbox state

  useEffect(() => {
    const fetchGalleries = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/galleri');
        if (response.ok) {
          const data = await response.json();
          setGalleries(data);
        }
      } catch (err) {
        console.error('Error fetching galleries:', err);
      }
    };

    fetchGalleries();
  }, []);

  // Get unique categories
  const categories = ['Alle', ...new Set(galleries.map(g => g.kategori.da))];

  // Filter galleries based on selected category
  const filteredGalleries = selectedCategory === 'Alle'
    ? galleries
    : galleries.filter(g => g.kategori.da === selectedCategory);

  return (
    <>
      <Helmet>
        <title>Billedgalleri | Se billeder af vores håndværk | Tømrerfirmaet RNS Djursland</title>
        <meta name="description" content="Se billeder af vores udførte tømrer- og malerarbejde på Djursland. Gå på opdagelse i vores gallerier inden for sommerhusrenovering, asbestarbejde, tag samt vinduer og døre." />
        <meta property="og:title" content="Billedgalleri | Se billeder af vores håndværk | Tømrerfirmaet RNS Djursland" />
        <meta property="og:description" content="Se billeder af vores udførte tømrer- og malerarbejde på Djursland. Gå på opdagelse i vores gallerier inden for sommerhusrenovering, asbestarbejde, tag samt vinduer og døre." />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/d1f69564-4a06-4909-b6f1-fa1f6ad6911e/image00125.jpeg" />
        <link rel="canonical" href="https://rns.dk/galleri" />
        <html lang="da-DK" />
      </Helmet>

      {/* Gallery Filter & Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 space-y-12">
          {/* Page Title */}
          <div className="text-center space-y-2 max-w-2xl mx-auto mb-10">
            <h1 className="font-display font-extrabold text-4xl text-slate-900">
              Vores Billedgalleri
            </h1>
            <p className="text-slate-600">
              Gå på opdagelse i vores udførte projekter og se kvaliteten af vores tømrer- og malerarbejde.
            </p>
          </div>

          {/* Category Filter Buttons */}
          <div className="flex flex-wrap justify-center items-center gap-x-8 gap-y-3 border-b border-black/10 pb-6">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`pb-2 text-sm font-bold uppercase tracking-wider transition-all duration-200 rounded-none border-b-2 cursor-pointer ${
                  selectedCategory === category
                    ? 'border-black text-black'
                    : 'border-transparent text-black/50 hover:text-black hover:border-black/30'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Galleries Grid */}
          <div className="space-y-16">
            {filteredGalleries.map((gallery) => (
              <div key={gallery.id} className="space-y-6">
                <div className="border-b border-slate-100 pb-4 flex items-center justify-between">
                  <h2 className="font-display font-bold text-2xl text-slate-900">
                    {gallery.titel.da}
                  </h2>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {gallery.billeder.map((imgUrl, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveImage(imgUrl)}
                      className="group relative h-40 sm:h-48 rounded-none overflow-hidden border border-slate-100 shadow-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-secondary"
                    >
                      <img
                        src={`https://assets.auxodev.dk/cdn-cgi/image/width=400,height=300,fit=cover,quality=80,format=auto/${imgUrl.replace('https://assets.auxodev.dk/', '')}`}
                        alt={`${gallery.titel?.da || gallery.titel} - Referencebillede ${idx + 1} fra Tømrerfirmaet RNS`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <i className="fa-solid fa-magnifying-glass-plus text-white text-lg" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {filteredGalleries.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <i className="fa-solid fa-images text-4xl mb-3 block text-slate-300" />
                Ingen gallerier fundet i denne kategori.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {activeImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setActiveImage(null)}
        >
          <button
            onClick={() => setActiveImage(null)}
            className="absolute top-6 right-6 text-white hover:text-brand-secondary text-3xl cursor-pointer focus:outline-none"
            aria-label="Luk galleri"
          >
            <i className="fa-solid fa-xmark" />
          </button>
          <div className="max-w-5xl max-h-[85vh] overflow-hidden rounded-none" onClick={(e) => e.stopPropagation()}>
            <img
              src={`https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/${activeImage.replace('https://assets.auxodev.dk/', '')}`}
              alt="Forstørret billede af tømrer- og malerarbejde fra Tømrerfirmaet RNS"
              className="w-full h-full object-contain max-h-[85vh] touch-pan-y"
            />
          </div>
        </div>
      )}
    </>
  );
}
